import { useEffect, useRef, useState } from "react";
import {
  Bold, FileText, Heading2, Heading3, List, ListOrdered, Loader2, Pencil, X,
} from "lucide-react";
import api from "../../lib/api";
import { saveSuccess, saveError } from "../../lib/saveBanner";

/**
 * TermsSection (v4.2.9) — Rental Terms & Conditions editor.
 * Lightweight contentEditable rich text (Bold, H2/H3, bullet & numbered
 * lists) stored as HTML in `business.terms_and_conditions`. The backend
 * increments `terms_version` on every content change and the PDF agreement
 * parses the markup. Blank falls back to the built-in clause set.
 */

const TOOLS = [
  { id: "bold", label: "Bold", Icon: Bold, cmd: ["bold"] },
  { id: "h2", label: "Heading 2", Icon: Heading2, cmd: ["formatBlock", "<h2>"] },
  { id: "h3", label: "Heading 3", Icon: Heading3, cmd: ["formatBlock", "<h3>"] },
  { id: "ul", label: "Bullet list", Icon: List, cmd: ["insertUnorderedList"] },
  { id: "ol", label: "Numbered list", Icon: ListOrdered, cmd: ["insertOrderedList"] },
];

const stripUnsafe = (html) =>
  html
    .replace(/<\s*(script|style|iframe)[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/\son\w+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "");

export default function TermsSection() {
  const [terms, setTerms] = useState("");
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => {
        setTerms(r.data?.terms_and_conditions || "");
        setVersion(r.data?.terms_version || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openEditor = () => {
    setEditing(true);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = terms || "<p><br/></p>";
        editorRef.current.focus();
      }
    }, 0);
  };

  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
  };

  const save = async () => {
    const draft = stripUnsafe(editorRef.current?.innerHTML || "").trim();
    setSaving(true);
    try {
      const { data: current } = await api.get("/admin/settings");
      delete current.id;
      delete current._id;
      const { data } = await api.put("/admin/settings", {
        ...current,
        terms_and_conditions: draft,
      });
      setTerms(data?.terms_and_conditions ?? draft);
      setVersion(data?.terms_version ?? version);
      setEditing(false);
      saveSuccess();
    } catch (e) {
      saveError(e?.response?.data?.detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="terms-panel"
    >
      <header className="px-6 py-4 flex items-center justify-between gap-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Section</p>
          <h2 className="font-display text-xl mt-1 flex items-center gap-2">
            <FileText size={18} /> Rental Terms &amp; Conditions
            <span
              className="text-[10px] font-sans tracking-wider uppercase px-2 py-0.5 rounded-full border border-black/15 dark:border-white/20 text-neutral-600 dark:text-neutral-300"
              data-testid="terms-version-badge"
            >
              v{version}
            </span>
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Printed on every PDF rental agreement. Leave blank to use the built-in default clauses.
          </p>
        </div>
        <button
          type="button"
          onClick={openEditor}
          className="shrink-0 inline-flex items-center gap-2 border border-black/15 dark:border-white/20 px-4 py-2 text-sm rounded hover:border-black dark:hover:border-white"
          data-testid="terms-edit-btn"
        >
          <Pencil size={14} /> Edit Terms
        </button>
      </header>

      <div className="p-6">
        {loading ? (
          <Loader2 className="animate-spin text-neutral-400" size={18} />
        ) : terms.trim() ? (
          <div
            className="terms-rich text-sm leading-relaxed text-neutral-700 dark:text-neutral-200 max-h-80 overflow-y-auto"
            data-testid="terms-preview"
            dangerouslySetInnerHTML={{ __html: terms }}
          />
        ) : (
          <p className="text-sm text-neutral-500 dark:text-neutral-400" data-testid="terms-empty">
            No custom terms saved — the default rental clauses are printed on agreements.
          </p>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm overflow-y-auto p-4 flex justify-center items-start"
          data-testid="terms-modal"
        >
          <div className="bg-white dark:bg-[#1F2937] w-full max-w-3xl rounded-lg shadow-2xl my-8">
            <header className="px-6 py-4 flex items-start justify-between border-b border-black/8 dark:border-white/10">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Agreement</p>
                <h3 className="font-display text-2xl mt-1">Edit rental terms</h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Use the toolbar for headings, bold and lists. Greek characters are supported.
                  Saving publishes revision v{version + 1}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-neutral-500 hover:text-black dark:hover:text-white"
                data-testid="terms-modal-close"
              >
                <X size={20} />
              </button>
            </header>

            <div className="p-6">
              <div
                className="flex items-center gap-1 border border-black/15 dark:border-white/20 rounded-t px-2 py-1.5 bg-[#F9FAFB] dark:bg-[#111827]"
                data-testid="terms-toolbar"
              >
                {TOOLS.map(({ id, label, Icon, cmd }) => (
                  <button
                    key={id}
                    type="button"
                    title={label}
                    aria-label={label}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => exec(...cmd)}
                    className="p-2 rounded hover:bg-black/10 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-200"
                    data-testid={`terms-tool-${id}`}
                  >
                    <Icon size={15} />
                  </button>
                ))}
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className="terms-rich w-full min-h-[340px] max-h-[55vh] overflow-y-auto bg-transparent border border-t-0 border-black/15 dark:border-white/20 rounded-b p-4 text-sm leading-relaxed outline-none focus:border-black dark:focus:border-white"
                data-testid="terms-editor"
              />
              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 text-sm border border-black/15 dark:border-white/20 rounded hover:border-black dark:hover:border-white"
                  data-testid="terms-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-black text-white dark:bg-white dark:text-black rounded hover:opacity-90 disabled:opacity-50"
                  data-testid="terms-save-btn"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />} Save terms
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
